import { ActivatedRoute } from '@angular/router';
import { LabTestResult } from '../../lab-test-results/lab-test-result';
import { publishReplay, refCount, map, filter as rxFilter, tap, debounceTime, withLatestFrom } from 'rxjs/operators';
import { ChangeDetectionStrategy, Component, OnDestroy } from '@angular/core';
import { LabResultsDataStorage } from './lab-results-data-storage';
import { ChartLegendItem } from '../../legend';
import { CvdChartData, CvdChartDataValue, BoxplotData, BoxPlotEvent } from '@covance/ng-cvd3';
import { Observable, BehaviorSubject, combineLatest } from 'rxjs';
import { canBeUsedInCharts, buildBoxPlotGenderLegend, clearOutliers } from '../../charts/chart-helper';
import { UrlBuilder } from '../../core';
import { SponsorRouteData } from '../sponsor-route-data';
import { removeAllTooltips } from '../../core/tooltip-remover';
import { isEmpty } from 'lodash';
import { BoxplotChartDataBuilder } from '../../charts/boxplot-chart-data-builder';
import { routeData } from '../../shared/route-helpers';
import { LabResultSponsorTooltipRenderer } from './lab-result-tooltip-renderer';

const showOnlyPointsMaxDataLength = 3;

@Component({
    selector: 'sponsor-boxplot',
    template: require('./sponsor-boxplot.component.html'),
    styles: [require('./../../on-screen-results/standard-test-results/lab-chart.scss')],
    changeDetection: ChangeDetectionStrategy.OnPush,
    providers: [LabResultSponsorTooltipRenderer],
})
export class SponsorBoxplotComponent implements OnDestroy {
    public sexLegendData: Observable<ChartLegendItem[]>;
    public boxPlotChartData: Observable<CvdChartData<CvdChartDataValue>[]>;
    public yAxisTitle: Observable<string>;
    public xAxisTitle: Observable<string>;

    public tooltip = this.labResultSponsorToolTipRenderer.boxtooltip;

    private readonly splitGenders = new BehaviorSubject(false);

    constructor(
        private storage: LabResultsDataStorage,
        private chartDataBuilder: BoxplotChartDataBuilder,
        private labResultSponsorToolTipRenderer: LabResultSponsorTooltipRenderer,
        private urlBuilder: UrlBuilder,
        private sponsorRoutes: SponsorRouteData,
        route: ActivatedRoute
    ) {
        const boxPlotGroupSelector$: Observable<(result: LabTestResult) => string> = route.data.pipe(map(d => d[routeData.boxPlotGroupSelector]));
        const boxPlotXAxisTickRenderrer$: Observable<(groupKey: string, groupedResults: LabTestResult[]) => string> = route.data.pipe(map(d => d[routeData.boxPlotXTickRenderrer]));
        const boxPlotXAxisSorting$: Observable<(results: LabTestResult[]) => number | string> = route.data.pipe(map(d => d[routeData.boxPlotXAxisSortingSelector]));
        const boxPlotDataExtraFilter$: Observable<(results: LabTestResult) => boolean> = route.data.pipe(map(d => d[routeData.boxPlotDataExtraFilter]));
        
        this.xAxisTitle = route.data.pipe(map(d => d[routeData.boxPlotXAxisTitle]));
        const testData = storage.labTestsData.pipe(
            withLatestFrom(boxPlotDataExtraFilter$),
            map(([results, extraFilter]) => results && results.filter(r => canBeUsedInCharts(r) && extraFilter(r))),
            publishReplay(1),
            refCount()
        );

        this.boxPlotChartData = combineLatest(
                testData, 
                this.splitGenders, 
                boxPlotGroupSelector$, 
                boxPlotXAxisTickRenderrer$,
                boxPlotXAxisSorting$)
                    .pipe(
                        map(([lr, sg, gs, tickRenderrer, xAxisSorting]) => 
                            this.buildChartData(lr, sg, gs, tickRenderrer, xAxisSorting)),
                        tap(() => clearOutliers()),
                        publishReplay(1),
                        refCount()
        );

        this.yAxisTitle = testData.pipe(
            rxFilter(td => !isEmpty(td)),
            map(tr => tr[0].testNameWithUnit),
            publishReplay(1),
            refCount()
        );
        // the chart should be renderred before creating a legend that's why we have debounceTime(0) here
        this.sexLegendData = this.boxPlotChartData.pipe(debounceTime(0), map((data) => buildBoxPlotGenderLegend(data, this.splitGenders.value)));
    }

    public xAxisSorting = () => 0;

    public onSplitToggle() {
        this.splitGenders.next(!this.splitGenders.value);
    }

    public isBoxVisible = (d: CvdChartDataValue) => showOnlyPointsMaxDataLength < (d.y as BoxplotData[]).length;

    public calcWhiskers = (d: BoxplotData[]) =>
        showOnlyPointsMaxDataLength < d.length ? null : [-1, -1]

    public goToSubject(event: BoxPlotEvent) {
        if (event.isBox) {
            return;
        }

        const labTestId = event.data.rows[0];
        const labTestResult = this.storage.testsDataSnapshotDictionary[labTestId];

        if (!labTestResult.zcSubjectId) {
            return;
        }

        this.urlBuilder
            .esa()
            .sponsor()
            .study(this.sponsorRoutes.snapshot.selectedStudy)
            .subjects(labTestResult.zcSubjectId)
            .navigate();
    }

    public ngOnDestroy() {
        removeAllTooltips();
    }

    private buildChartData(
        data: any[], 
        splitGender: boolean, 
        groupSelector: (result: LabTestResult) => string, 
        xTickRenderrer: (groupKey: string, groupedResults: LabTestResult[]) => string,
        xAxisSorting: (results: LabTestResult[]) => number | string): CvdChartData<CvdChartDataValue>[] {
        const cvdData = this.chartDataBuilder.buildChartData(data, splitGender, groupSelector, xTickRenderrer, xAxisSorting);
        return cvdData;
    }
}
